/**
 * SortableList — ein dünner Wrapper um @dnd-kit/sortable.
 *
 * Zweck: macht eine Liste (Array) von Editor-Karten drag&drop-reorderbar.
 * Jede Karte kriegt einen <DragHandle/>-Child den du an deine UI pinnen kannst,
 * per `render({dragHandleProps, isDragging})`-Prop.
 *
 * Usage:
 *   <SortableList
 *     items={services.map((s, i) => i)}
 *     onReorder={(newOrder) => setServices(reorder(services, newOrder))}
 *     renderItem={(idx, {dragHandleProps}) => (
 *       <div>
 *         <button {...dragHandleProps}><GripVertical/></button>
 *         <Input value={services[idx].title} … />
 *       </div>
 *     )}
 *   />
 */
import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Eine einzelne sortierbare Zeile. Stellt `dragHandleProps` + `isDragging`
 * über `renderItem` bereit.
 */
function SortableRow({ id, index, renderItem, itemKey }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 20 : 1,
  };

  const dragHandleProps = {
    ref: setActivatorNodeRef,
    ...attributes,
    ...listeners,
    "data-testid": `drag-handle-${itemKey || id}-${index}`,
    className: "cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center h-6 w-6 -ml-1 rounded-md",
    title: "Zum Umsortieren ziehen",
    "aria-label": "Drag Handle",
    type: "button",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {renderItem(index, { dragHandleProps, isDragging })}
    </div>
  );
}

/**
 * Wrapper für eine ganze Liste.
 * `items`: Array der Index-IDs (nummerisch, muss eindeutig in dieser Liste sein).
 * `onReorder(newIndexOrder)`: callback bekommt neue Index-Reihenfolge.
 * `renderItem(idx, {dragHandleProps, isDragging})`: rendert eine einzelne Karte.
 * `itemKey`: Präfix für data-testids (z.B. "service", "testimonial").
 */
export const SortableList = ({ items, onReorder, renderItem, itemKey = "item" }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.indexOf(active.id);
    const newIdx = items.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorder(arrayMove(items, oldIdx, newIdx));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="space-y-2" data-testid={`sortable-list-${itemKey}`}>
          {items.map((id, index) => (
            <SortableRow key={id} id={id} index={id} renderItem={renderItem} itemKey={itemKey} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

// Default-Drag-Handle-Icon für Konsistenz
export const DragHandleIcon = (props) => <GripVertical size={14} {...props} />;

/**
 * Helper: reorder ein Array nach einer Permutation von Indizes.
 * Beispiel: reorder(["a","b","c"], [2,0,1]) → ["c","a","b"]
 */
export const reorderByIndexArray = (arr, newIndexOrder) =>
  newIndexOrder.map((i) => arr[i]);
